import React from 'react';
import { osc, wobble, drawOn, stagger, loop, clamp, pathLerp, lerp, easeOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES (Anivaze v2) — weather edition.
 * The concept HAPPENS in front of you; no labels or diagrams.
 * Pure function of `t` (deterministic + exportable). See
 * design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ━━━ Rainbow: sun + rain → rainbow draws on, character points up ━━━━━━━━━━━━ */

export interface RainbowSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since scene start — the ONLY source of motion. */
  t?: number;
}

// 7-colour arc definition (outermost first)
const RAINBOW_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#6366f1', '#a855f7'];
// Loop period (seconds)
const RAINBOW_PERIOD = 7;

export const RainbowScene: React.FC<RainbowSceneProps> = ({
  opacity = 1,
  accentColor = '#fbbf24',
  label = 'Sun + rain makes a rainbow!',
  t = 0,
}) => {
  // Phase within the 7-second loop: 0→1
  const phase = loop(t, RAINBOW_PERIOD);

  // ── Rain phase: 0–0.45 of cycle
  const rainPhase = clamp(phase / 0.45, 0, 1);
  const rainOpacity = rainPhase < 0.85 ? 1 : 1 - (rainPhase - 0.85) / 0.15;

  // ── Rainbow draws on during 0.35–0.80 of cycle
  const tInCycle = (t % RAINBOW_PERIOD);
  const rainbowDelay = RAINBOW_PERIOD * 0.35;
  const rainbowDur   = RAINBOW_PERIOD * 0.35;
  const rainbowProgress = clamp((tInCycle - rainbowDelay) / rainbowDur, 0, 1);
  const rbDrawn = easeOut(rainbowProgress);

  // ── Sun brightness: glows fully once rainbow is visible
  const sunGlow = osc(t, 2.2, 0.7, 1.0);
  const sunScale = 1 + osc(t, 2.2, 0, 0.08);

  // ── Cloud sway
  const cloudX = 530 + wobble(t, 3.1, 6);
  const cloudY = 120;

  // ── Rain drops — deterministic positions derived from index
  const drops = Array.from({ length: 18 }, (_, i) => {
    const col = i % 6;
    const row = Math.floor(i / 6);
    const baseX = cloudX - 55 + col * 22 + (row % 2) * 11;
    const speed = 0.9 + (i % 4) * 0.08;
    const yFrac = loop(t * speed + i * 0.17, 1);
    const dropY = cloudY + 38 + yFrac * 180;
    return { x: baseX, y: dropY, alpha: rainOpacity * 0.85 };
  });

  // ── Happy character (bottom-left) arm rises to point up when rainbow appears
  const armAngle = lerp(-10, -50, rbDrawn);   // degrees above horizontal
  const mouthCurve = lerp(4, 12, rbDrawn);    // smile gets bigger
  const charX = 130, charY = 370;

  // ── Caption pill
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="rw-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bae6fd" />
          <stop offset="100%" stopColor="#e0f2fe" />
        </linearGradient>
        <radialGradient id="rw-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fff7cc" />
          <stop offset="70%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={390} fill="url(#rw-sky)" />
      {/* Ground (green hills) */}
      <ellipse cx={160} cy={450} rx={220} ry={90} fill="#4ade80" />
      <ellipse cx={500} cy={460} rx={340} ry={80} fill="#22c55e" />
      <rect x={0} y={400} width={800} height={50} fill="#16a34a" />

      {/* ── Sun (top-left) */}
      <g transform={`translate(120 90) scale(${sunScale})`} opacity={sunGlow}>
        <g transform={`rotate(${(t * 7) % 360})`}>
          {Array.from({ length: 12 }, (_, k) => {
            const a = (k / 12) * Math.PI * 2;
            return (
              <line
                key={k}
                x1={38 * Math.cos(a)} y1={38 * Math.sin(a)}
                x2={54 * Math.cos(a)} y2={54 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3} strokeLinecap="round"
              />
            );
          })}
        </g>
        <circle r={30} fill="url(#rw-sun)" />
        {/* Sun smile */}
        <path d="M-10,8 Q0,18 10,8" fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={-8} cy={2} r={2.5} fill="#92400e" />
        <circle cx={8}  cy={2} r={2.5} fill="#92400e" />
      </g>

      {/* ── Rain cloud */}
      <g transform={`translate(${cloudX} ${cloudY})`}>
        <ellipse cx={0}   cy={0}  rx={48} ry={28} fill="#94a3b8" />
        <ellipse cx={44}  cy={8}  rx={36} ry={22} fill="#94a3b8" />
        <ellipse cx={-38} cy={10} rx={30} ry={20} fill="#94a3b8" />
        <ellipse cx={8}   cy={14} rx={50} ry={22} fill="#64748b" />
      </g>

      {/* ── Raindrops */}
      {drops.map((d, i) => (
        <ellipse
          key={i}
          cx={d.x} cy={d.y}
          rx={2.8} ry={5.5}
          fill="#60a5fa"
          opacity={d.alpha}
        />
      ))}

      {/* ── Rainbow — 7 arcs drawn on from left to right */}
      {RAINBOW_COLORS.map((color, ci) => {
        const R = 230 - ci * 18;   // outermost arc is largest
        // arc: semi-circle centred at (400, 395), only the top half
        // We draw from left (180°) to right (0°) using strokeDasharray trick.
        const arcLen = Math.PI * R;         // half-circumference
        const drawn = arcLen * rbDrawn * clamp(1 - stagger(ci, 0.04), 0.6, 1);
        return (
          <path
            key={ci}
            d={`M ${400 - R} 395 A ${R} ${R} 0 0 1 ${400 + R} 395`}
            fill="none"
            stroke={color}
            strokeWidth={10}
            strokeLinecap="round"
            strokeDasharray={`${drawn} ${arcLen}`}
            opacity={rbDrawn > 0.02 ? 0.9 : 0}
          />
        );
      })}

      {/* ── Happy character (bottom-left): round body, pointing arm */}
      <g transform={`translate(${charX} ${charY})`}>
        {/* shadow */}
        <ellipse cx={0} cy={42} rx={28} ry={7} fill="rgba(0,0,0,0.15)" />
        {/* body */}
        <ellipse cx={0} cy={14} rx={22} ry={28} fill="#fdba74" />
        {/* head */}
        <circle cx={0} cy={-26} r={20} fill="#fde68a" />
        {/* eyes */}
        <circle cx={-7} cy={-28} r={3} fill="#1f2937" />
        <circle cx={7}  cy={-28} r={3} fill="#1f2937" />
        {/* smile grows as rainbow appears */}
        <path
          d={`M-9,-18 Q0,${-18 + mouthCurve} 9,-18`}
          fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round"
        />
        {/* left arm (idle, hangs down) */}
        <line x1={-20} y1={5} x2={-34} y2={22} stroke="#fdba74" strokeWidth={7} strokeLinecap="round" />
        {/* right arm — rises to point at rainbow */}
        <g transform={`rotate(${armAngle} 20 5)`}>
          <line x1={20} y1={5} x2={40} y2={5} stroke="#fdba74" strokeWidth={7} strokeLinecap="round" />
          {/* pointing finger */}
          <circle cx={43} cy={5} r={4} fill="#fde68a" />
        </g>
        {/* legs */}
        <line x1={-9} y1={40} x2={-12} y2={60} stroke="#fdba74" strokeWidth={7} strokeLinecap="round" />
        <line x1={9}  y1={40} x2={12}  y2={60} stroke="#fdba74" strokeWidth={7} strokeLinecap="round" />
      </g>

      {/* Caption */}
      <g transform="translate(400 428)">
        <rect x={-230} y={-20} width={460} height={36} rx={18} fill="rgba(255,255,255,0.90)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};

/* ━━━ Volcano: magma rises, erupts, lava flows, then resets ━━━━━━━━━━━━━━━━━ */

export interface VolcanoSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since scene start — the ONLY source of motion. */
  t?: number;
}

// Vent path: magma travels from chamber up through the vent neck
const VENT_PATH = [
  { x: 400, y: 360 },   // chamber top
  { x: 400, y: 280 },   // lower vent
  { x: 400, y: 210 },   // upper vent
  { x: 400, y: 160 },   // crater mouth
];

const VOLCANO_PERIOD = 7;

export const VolcanoScene: React.FC<VolcanoSceneProps> = ({
  opacity = 1,
  accentColor = '#ef4444',
  label = 'Hot melted rock bursts out!',
  t = 0,
}) => {
  const phase = loop(t, VOLCANO_PERIOD);    // 0→1 each 7 s

  // ── Magma glow: chamber pulses brighter as it builds (0–0.45)
  const buildPhase  = clamp(phase / 0.45, 0, 1);
  const chamberGlow = osc(t, 1.1, 0.55, 1.0);

  // ── Eruption: fires at phase ~0.45–0.80
  const eruptStart = 0.45, eruptEnd = 0.80;
  const eruptPhase = clamp((phase - eruptStart) / (eruptEnd - eruptStart), 0, 1);

  // ── Settle/cool: 0.80–1.0
  const settlePhase = clamp((phase - 0.80) / 0.20, 0, 1);

  // ── Travelling magma dots up the vent (3 dots staggered)
  const ventDots = Array.from({ length: 3 }, (_, i) => {
    const p = loop(t * 0.55 + stagger(i, 0.33), 1);
    const inVent = p < 0.92;   // disappear near crater
    const pos = pathLerp(VENT_PATH, p);
    return { ...pos, alpha: inVent ? clamp(buildPhase * 1.4, 0, 1) : 0 };
  });

  // ── Lava fountain: arc of blobs erupting upward
  const fountainCount = 8;
  const fountainBlobs = Array.from({ length: fountainCount }, (_, i) => {
    const angle = lerp(-75, -105, i / (fountainCount - 1));   // spray arc
    const speed  = 80 + (i % 3) * 30;
    const rad = (angle * Math.PI) / 180;
    const tErupt = eruptPhase * 1.6;                          // faster than cycle
    // each blob shoots out and falls (parabolic)
    const blobT = loop(tErupt + stagger(i, 0.12), 1);
    const bx = 400 + speed * blobT * Math.cos(rad);
    const by = 160 + speed * blobT * Math.sin(rad) + 90 * blobT * blobT;  // gravity
    const alpha = eruptPhase > 0.04 ? clamp(1 - blobT * 0.7, 0, 1) : 0;
    return { x: bx, y: by, r: 6 + (i % 3) * 3, alpha };
  });

  // ── Lava flows down sides after eruption
  const lavaFlowL = clamp(eruptPhase * 1.6, 0, 1);
  const lavaFlowR = clamp((eruptPhase - 0.08) * 1.6, 0, 1);
  // left flow path along left flank
  const leftFlowPts = [
    { x: 380, y: 168 }, { x: 330, y: 230 }, { x: 270, y: 305 }, { x: 200, y: 385 },
  ];
  const rightFlowPts = [
    { x: 420, y: 168 }, { x: 470, y: 230 }, { x: 530, y: 305 }, { x: 600, y: 385 },
  ];
  const lavaLTip = pathLerp(leftFlowPts, lavaFlowL);
  const lavaRTip = pathLerp(rightFlowPts, lavaFlowR);

  // Build SVG path strings for partial flow lines (just tip dot, looks cleaner)
  // We'll draw a polyline up to the tip instead.
  const leftFlowSegPts = leftFlowPts.slice(0, Math.ceil(lavaFlowL * (leftFlowPts.length - 1)) + 1);
  const rightFlowSegPts = rightFlowPts.slice(0, Math.ceil(lavaFlowR * (rightFlowPts.length - 1)) + 1);

  // ── Smoke/ash puffs
  const puffs = Array.from({ length: 5 }, (_, i) => {
    const puffPhase = loop(t * 0.6 + stagger(i, 0.2), 1);
    const px = 400 + wobble(t + i, 2.5 + i * 0.3, 18 + i * 8);
    const py = lerp(140, 40, puffPhase);
    const pr = lerp(8, 28 + i * 4, puffPhase);
    const alpha = eruptPhase > 0.1
      ? clamp(eruptPhase * 1.2, 0, 1) * (1 - puffPhase * 0.85)
      : 0;
    return { x: px, y: py, r: pr, alpha };
  });

  // ── Chamber glow radius (breathes)
  const chamberR = 62 + osc(t, 1.1, -4, 4);

  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="vc-chamber" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="55%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#7f1d1d" />
        </radialGradient>
        <linearGradient id="vc-lava" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
        <linearGradient id="vc-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>
        <linearGradient id="vc-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#92400e" />
          <stop offset="100%" stopColor="#78350f" />
        </linearGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={450} fill="url(#vc-sky)" />

      {/* Ground/base */}
      <ellipse cx={400} cy={410} rx={380} ry={55} fill="#78350f" />
      <rect x={0} y={400} width={800} height={50} fill="#78350f" />

      {/* ── Volcano body (cross-section silhouette) */}
      {/* Outer mountain shape */}
      <path
        d="M160,400 L270,240 Q310,185 370,168 L400,160 L430,168 Q490,185 530,240 L640,400 Z"
        fill="#44403c"
        stroke="#292524"
        strokeWidth={2}
      />
      {/* Inner cross-section fill (lighter rock) */}
      <path
        d="M200,400 L295,255 Q330,200 385,172 L400,165 L415,172 Q470,200 505,255 L600,400 Z"
        fill="#57534e"
      />

      {/* ── Vent channel */}
      <path
        d="M385,400 Q388,320 390,210 Q392,175 400,160 Q408,175 410,210 Q412,320 415,400 Z"
        fill="#292524"
      />

      {/* ── Magma chamber glow */}
      <ellipse
        cx={400} cy={385}
        rx={chamberR} ry={chamberR * 0.45}
        fill="url(#vc-chamber)"
        opacity={chamberGlow}
      />

      {/* ── Travelling magma dots up the vent */}
      {ventDots.map((d, i) => (
        <circle
          key={i}
          cx={d.x} cy={d.y}
          r={5 + (i % 2) * 2}
          fill="#fbbf24"
          opacity={d.alpha}
        />
      ))}

      {/* ── Lava flows down the sides */}
      {lavaFlowL > 0.02 && (
        <>
          <polyline
            points={leftFlowSegPts.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#ef4444"
            strokeWidth={10}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.92}
          />
          <circle cx={lavaLTip.x} cy={lavaLTip.y} r={9} fill="#fbbf24" opacity={0.95} />
        </>
      )}
      {lavaFlowR > 0.02 && (
        <>
          <polyline
            points={rightFlowSegPts.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#ef4444"
            strokeWidth={10}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.92}
          />
          <circle cx={lavaRTip.x} cy={lavaRTip.y} r={9} fill="#fbbf24" opacity={0.95} />
        </>
      )}

      {/* ── Fountain blobs */}
      {fountainBlobs.map((b, i) => (
        <circle
          key={i}
          cx={b.x} cy={b.y}
          r={b.r}
          fill={i % 2 === 0 ? '#fbbf24' : accentColor}
          opacity={b.alpha}
        />
      ))}

      {/* ── Smoke / ash puffs */}
      {puffs.map((p, i) => (
        <circle
          key={i}
          cx={p.x} cy={p.y}
          r={p.r}
          fill="#94a3b8"
          opacity={p.alpha}
        />
      ))}

      {/* ── Crater rim highlight */}
      <ellipse
        cx={400} cy={162}
        rx={22 + osc(t, 1.4, 0, 6) * eruptPhase}
        ry={8}
        fill="#ef4444"
        opacity={0.4 + eruptPhase * 0.5}
      />

      {/* Caption */}
      <g transform="translate(400 430)">
        <rect x={-210} y={-20} width={420} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#fca5a5" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};
